import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Loader2,
  Mail,
  Phone,
  MapPin,
  Search,
  LayoutGrid,
  List,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Building2
} from 'lucide-react';
import ViewerPartnersView from '@/components/ViewerPartnersView';

import { API } from '../utils/api';

const Investors = () => {
  const { getAuthHeaders, user } = useAuth();
  const [investors, setInvestors] = useState([]);
  const [ownerships, setOwnerships] = useState([]);
  const [llcs, setLlcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('partnersViewMode') || 'list';
  });
  const [sortConfig, setSortConfig] = useState(() => {
    const saved = localStorage.getItem('partnersSortConfig');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return { key: 'name', direction: 'asc' };
      }
    }
    return { key: 'name', direction: 'asc' };
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'USA',
    notes: '',
    bank_name: '',
    bank_account_name: '',
    bank_account_number: '',
    bank_routing_number: '',
    bank_account_type: ''
  });

  const fetchData = async () => {
    try {
      const [investorsRes, ownershipsRes, llcsRes] = await Promise.all([
        axios.get(`${API}/investors`, { headers: getAuthHeaders() }),
        axios.get(`${API}/ownerships`, { headers: getAuthHeaders() }),
        axios.get(`${API}/llcs`, { headers: getAuthHeaders() })
      ]);
      setInvestors(investorsRes.data);
      setOwnerships(ownershipsRes.data);
      setLlcs(llcsRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load investors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Save view mode and sort config to localStorage
  useEffect(() => {
    localStorage.setItem('partnersViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem('partnersSortConfig', JSON.stringify(sortConfig));
  }, [sortConfig]);

  const getInvestorOwnerships = (investorId) => {
    return ownerships
      .filter(o => o.investor_id === investorId)
      .sort((a, b) => (a.llc_name || '').localeCompare(b.llc_name || ''));
  };

  // Group ownerships by LLC for viewer display
  const getOwnershipsByLLC = () => {
    const llcMap = {};
    ownerships.forEach(ownership => {
      const llcId = ownership.llc_id;
      // Find the LLC data to get total_assets
      const llcData = llcs.find(l => l.id === llcId);
      if (!llcMap[llcId]) {
        llcMap[llcId] = {
          llc_id: llcId,
          llc_name: ownership.llc_name,
          total_assets: llcData?.total_assets || 0,
          hide_percentages: llcData?.hide_percentages || false,
          partners: []
        };
      }
      // Find the investor for this ownership
      const investor = investors.find(inv => inv.id === ownership.investor_id);
      if (investor) {
        llcMap[llcId].partners.push({
          ...investor,
          percentage: ownership.percentage
        });
      }
    });
    // Sort LLCs by name and partners alphabetically by name
    return Object.values(llcMap)
      .sort((a, b) => a.llc_name.localeCompare(b.llc_name))
      .map(llc => ({
        ...llc,
        partners: llc.partners.sort((a, b) => a.name.localeCompare(b.name))
      }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      if (selectedInvestor) {
        await axios.put(`${API}/investors/${selectedInvestor.id}`, formData, { headers: getAuthHeaders() });
        toast.success('Investor updated successfully');
      } else {
        await axios.post(`${API}/investors`, formData, { headers: getAuthHeaders() });
        toast.success('Investor created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save investor');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = (investor) => {
    setSelectedInvestor(investor);
    setFormData({
      name: investor.name,
      email: investor.email,
      phone: investor.phone || '',
      address: investor.address || '',
      city: investor.city || '',
      state: investor.state || '',
      zip_code: investor.zip_code || '',
      country: investor.country || 'USA',
      notes: investor.notes || '',
      bank_name: investor.bank_name || '',
      bank_account_name: investor.bank_account_name || '',
      bank_account_number: investor.bank_account_number || '',
      bank_routing_number: investor.bank_routing_number || '',
      bank_account_type: investor.bank_account_type || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedInvestor) return;
    
    try {
      await axios.delete(`${API}/investors/${selectedInvestor.id}`, { headers: getAuthHeaders() });
      toast.success('Investor deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedInvestor(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete investor');
    }
  };

  const resetForm = () => {
    setSelectedInvestor(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'USA',
      notes: '',
      bank_name: '',
      bank_account_name: '',
      bank_account_number: '',
      bank_routing_number: '',
      bank_account_type: ''
    });
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const filteredInvestors = investors.filter(inv =>
    inv.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    inv.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedInvestors = [...filteredInvestors].sort((a, b) => {
    let aVal = a[sortConfig.key];
    let bVal = b[sortConfig.key];
    
    if (aVal === null || aVal === undefined) aVal = '';
    if (bVal === null || bVal === undefined) bVal = '';
    
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Sort icon helper - returns appropriate icon based on sort state
  const getSortIcon = (column) => {
    if (sortConfig.key === column) {
      return sortConfig.direction === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />;
    }
    return <ArrowUpDown className="w-4 h-4 opacity-50" />;
  };

  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'manager';
  const canEdit = user?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'manager';
  const isViewer = user?.role === 'viewer';
  const isProfessional = user?.role === 'professional';
  const llcGroupedData = isViewer ? getOwnershipsByLLC() : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="investors-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-serif font-semibold text-slate-900">
            {isViewer ? 'LLC Partners' : 'Partners'}
          </h1>
          <p className="text-slate-500 mt-1">
            {isViewer ? 'View your LLC ownership and partners' : 'Manage partner profiles and contact information'}
          </p>
        </div>
        {/* View Toggle for Viewers */}
        {isViewer && (
          <div className="flex border border-slate-200 rounded-lg overflow-hidden">
            <Button
              variant={viewMode === 'cards' ? 'default' : 'ghost'}
              size="sm"
              className={`rounded-none ${viewMode === 'cards' ? 'bg-slate-900 text-white' : ''}`}
              onClick={() => setViewMode('cards')}
            >
              <LayoutGrid className="w-4 h-4 mr-1" />
              Cards
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              className={`rounded-none ${viewMode === 'list' ? 'bg-slate-900 text-white' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <List className="w-4 h-4 mr-1" />
              List
            </Button>
          </div>
        )}
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-investor-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Partner
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px] max-h-[85vh] flex flex-col">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">
                  {selectedInvestor ? 'Edit Partner' : 'Add New Partner'}
                </DialogTitle>
                <DialogDescription>
                  {selectedInvestor ? 'Update partner information' : 'Enter partner details'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
                <div className="grid grid-cols-2 gap-4 py-4 pr-2">
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Smith"
                      required
                      data-testid="investor-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@example.com"
                      required
                      data-testid="investor-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      data-testid="investor-phone-input"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="address">Street Address</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="123 Main Street"
                      data-testid="investor-address-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="New York"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-2">
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                        placeholder="NY"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="zip">ZIP</Label>
                      <Input
                        id="zip"
                        value={formData.zip_code}
                        onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                        placeholder="10001"
                      />
                    </div>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Additional notes about this investor..."
                      rows={3}
                    />
                  </div>
                  
                  {/* Bank Information Section */}
                  <div className="col-span-2 pt-4 border-t border-slate-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Building2 className="w-4 h-4 text-slate-500" />
                      <span className="text-sm font-medium text-slate-700">Bank Information (for ACH/Direct Deposit)</span>
                    </div>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="bank_name">Bank Name</Label>
                    <Input
                      id="bank_name"
                      value={formData.bank_name}
                      onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                      placeholder="First National Bank"
                      data-testid="investor-bank-name-input"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="bank_account_name">Account Name (as it appears on account)</Label>
                    <Input
                      id="bank_account_name"
                      value={formData.bank_account_name}
                      onChange={(e) => setFormData({ ...formData, bank_account_name: e.target.value })}
                      placeholder="John Smith"
                      data-testid="investor-bank-account-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bank_routing_number">Routing Number</Label>
                    <Input
                      id="bank_routing_number"
                      value={formData.bank_routing_number}
                      onChange={(e) => setFormData({ ...formData, bank_routing_number: e.target.value })}
                      placeholder="123456789"
                      data-testid="investor-bank-routing-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bank_account_number">Account Number</Label>
                    <Input
                      id="bank_account_number"
                      value={formData.bank_account_number}
                      onChange={(e) => setFormData({ ...formData, bank_account_number: e.target.value })}
                      placeholder="••••••••1234"
                      data-testid="investor-bank-account-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bank_account_type">Account Type</Label>
                    <Select 
                      value={formData.bank_account_type} 
                      onValueChange={(value) => setFormData({ ...formData, bank_account_type: value })}
                    >
                      <SelectTrigger data-testid="investor-bank-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="checking">Checking</SelectItem>
                        <SelectItem value="savings">Savings</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter className="pt-4 border-t mt-4">
                  <Button type="button" variant="outline" onClick={() => {
                    setDialogOpen(false);
                    resetForm();
                  }}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-slate-900 hover:bg-slate-800"
                    disabled={formLoading}
                    data-testid="investor-submit-btn"
                  >
                    {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (selectedInvestor ? 'Update' : 'Create')}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search and View Toggle - Only show for admins */}
      {!isViewer && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search investors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="investor-search-input"
            />
          </div>
          <div className="flex border border-slate-200 rounded-lg overflow-hidden">
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              className={`rounded-none ${viewMode === 'list' ? 'bg-slate-900 text-white' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <List className="w-4 h-4 mr-1" />
              List
            </Button>
            <Button
              variant={viewMode === 'cards' ? 'default' : 'ghost'}
              size="sm"
              className={`rounded-none ${viewMode === 'cards' ? 'bg-slate-900 text-white' : ''}`}
              onClick={() => setViewMode('cards')}
            >
              <LayoutGrid className="w-4 h-4 mr-1" />
              Cards
            </Button>
          </div>
        </div>
      )}

      {/* Viewer LLC-Grouped View */}
      {isViewer && <ViewerPartnersView llcGroupedData={llcGroupedData} viewMode={viewMode} />}

      {/* Admin Empty State */}
      {!isViewer && sortedInvestors.length === 0 && (
        <Card className="border-dashed border-2 border-slate-300">
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="font-serif text-lg font-semibold text-slate-700">
              {searchQuery ? 'No partners found' : 'No partners yet'}
            </h3>
            <p className="text-slate-500 mt-1 mb-4">
              {searchQuery ? 'Try a different search term' : 'Get started by adding your first partner'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Admin List View */}
      {!isViewer && sortedInvestors.length > 0 && viewMode === 'list' && (
        <Card className="border-slate-200 overflow-hidden">
          <Table>
            <TableHeader style={{ backgroundColor: '#1e293b' }}>
              <TableRow style={{ backgroundColor: '#1e293b', borderBottom: '1px solid #334155' }}>
                <TableHead 
                  className="cursor-pointer hover:bg-slate-700 font-semibold py-3" 
                  style={{ color: '#f1f5f9', backgroundColor: '#1e293b' }}
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center gap-2">Name {getSortIcon('name')}</div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-slate-700 font-semibold py-3" 
                  style={{ color: '#f1f5f9', backgroundColor: '#1e293b' }}
                  onClick={() => handleSort('email')}
                >
                  <div className="flex items-center gap-2">Email {getSortIcon('email')}</div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-slate-700 font-semibold py-3" 
                  style={{ color: '#f1f5f9', backgroundColor: '#1e293b' }}
                  onClick={() => handleSort('phone')}
                >
                  <div className="flex items-center gap-2">Phone {getSortIcon('phone')}</div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-slate-700 font-semibold py-3" 
                  style={{ color: '#f1f5f9', backgroundColor: '#1e293b' }}
                  onClick={() => handleSort('city')}
                >
                  <div className="flex items-center gap-2">Location {getSortIcon('city')}</div>
                </TableHead>
                <TableHead className="font-semibold py-3" style={{ color: '#f1f5f9', backgroundColor: '#1e293b' }}>
                  LLC Ownership
                </TableHead>
                {isAdmin && (
                  <TableHead className="w-[100px] font-semibold py-3" style={{ color: '#f1f5f9', backgroundColor: '#1e293b' }}>
                    Actions
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedInvestors.map((investor) => {
                const investorOwnerships = getInvestorOwnerships(investor.id);
                return (
                  <TableRow key={investor.id}>
                    <TableCell className="font-medium">{investor.name}</TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1 text-slate-600">
                        <Mail className="w-3 h-3" />
                        {investor.email}
                      </span>
                    </TableCell>
                    <TableCell>
                      {investor.phone ? (
                        <span className="flex items-center gap-1 text-slate-600">
                          <Phone className="w-3 h-3" />
                          {investor.phone}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {investor.city || investor.state ? (
                        <span className="flex items-center gap-1 text-slate-600">
                          <MapPin className="w-3 h-3" />
                          {[investor.city, investor.state].filter(Boolean).join(', ')}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {investorOwnerships.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {investorOwnerships.map((own) => (
                            <Badge key={own.id} variant="outline" className="text-xs">
                              {own.llc_name}: {Number(own.percentage).toFixed(2)}%
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleEdit(investor)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-rose-600 hover:text-rose-700"
                            onClick={() => {
                              setSelectedInvestor(investor);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Admin Cards View */}
      {!isViewer && sortedInvestors.length > 0 && viewMode === 'cards' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedInvestors.map((investor, index) => {
            const investorOwnerships = getInvestorOwnerships(investor.id);
            return (
              <Card key={investor.id} className="border-slate-200 card-hover animate-fade-in" style={{ animationDelay: `${index * 0.05}s` }}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg font-serif">{investor.name}</CardTitle>
                      <CardDescription className="flex items-center gap-1 mt-1">
                        <Mail className="w-3 h-3" />
                        {investor.email}
                      </CardDescription>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleEdit(investor)}
                          data-testid={`edit-investor-${investor.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-rose-600 hover:text-rose-700"
                          onClick={() => {
                            setSelectedInvestor(investor);
                            setDeleteDialogOpen(true);
                          }}
                          data-testid={`delete-investor-${investor.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {investor.phone && (
                    <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                      <Phone className="w-4 h-4" />
                      {investor.phone}
                    </div>
                  )}
                  {(investor.city || investor.state) && (
                    <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
                      <MapPin className="w-4 h-4" />
                      {[investor.city, investor.state].filter(Boolean).join(', ')}
                    </div>
                  )}
                  
                  {investorOwnerships.length > 0 && (
                    <div className="pt-3 border-t border-slate-100">
                      <p className="text-xs text-slate-500 mb-2">LLC Ownership</p>
                      <div className="flex flex-wrap gap-1">
                        {investorOwnerships.map((own) => (
                          <Badge key={own.id} variant="outline" className="text-xs">
                            {own.llc_name}: {Number(own.percentage).toFixed(2)}%
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Partner?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {selectedInvestor?.name} and remove all their ownership records. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedInvestor(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-rose-600 hover:bg-rose-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Investors;
